import * as Phaser from "phaser";
import { SettingsGlobalPlugin } from "$/game/plugins/SettingsGlobalPlugin.ts";
import { assets, audiosprites } from "$/keys";

// Тип для извлечения всех строковых значений из assets.sounds
type SoundValue<T> = T extends string ? T
  : T extends Record<string, unknown> ? SoundValue<T[keyof T]>
  : never;

type SoundKey = SoundValue<typeof assets.sounds>;

type SpriteData = Record<string, { start: number; end: number; loop: boolean }>;

interface PlaySoundOptions {
  volume?: number;
  seek?: number;
}

export class SoundManagerGlobalPlugin extends Phaser.Plugins.BasePlugin {
  private settings!: SettingsGlobalPlugin;
  private currentMusic: Phaser.Sound.BaseSound | null = null;
  private spriteData: SpriteData | null = null;
  private initialized = false;

  constructor(pluginManager: Phaser.Plugins.PluginManager) {
    super(pluginManager);
    this.settings = pluginManager.game.plugins.get(
      "settings",
    ) as SettingsGlobalPlugin;
  }

  /**
   * Initialize audiosprite data after loading
   */
  public init(): void {
    if (this.initialized) return;

    const scene = this.game.scene.getScene("LoadingScene");
    if (!scene) return;

    // Get spritemap data from JSON cache
    const dataKey = `${audiosprites.sfx.key}-data`;
    const data = scene.cache.json.get(dataKey);

    if (data && data.spritemap) {
      this.spriteData = data.spritemap;
    }

    this.initialized = true;
  }

  public playSound = (
    key: SoundKey,
    options: PlaySoundOptions = {},
  ) => {
    if (!this.settings.getSetting("isSoundEnabled")) return;

    // Lazy init if not initialized yet
    if (!this.initialized) {
      this.init();
    }

    // Check if audio is loaded
    if (!this.game.cache.audio.exists(audiosprites.sfx.key)) {
      return;
    }

    if (!this.spriteData) {
      return;
    }

    const { volume = 0.5, seek = 0 } = options;

    // Get marker data from spritemap
    const spriteInfo = this.spriteData[key];
    if (!spriteInfo) {
      console.warn(`Sound "${key}" not found in audiosprite`);
      return;
    }

    // Create sound from audiosprite
    const sound = this.game.sound.add(audiosprites.sfx.key, {
      volume,
    });

    // Play with seek position
    sound.play({
      seek: spriteInfo.start + seek,
    });

    // Stop and destroy after duration
    const duration = spriteInfo.end - spriteInfo.start;
    const durationMs = duration * 1000;

    setTimeout(() => {
      sound.stop();
      sound.destroy();
    }, durationMs + 100);
  };

  public playMusic = (key: SoundKey) => {
    if (!this.settings.getSetting("isMusicEnabled")) {
      return;
    }

    // Check if audio is loaded
    if (!this.game.cache.audio.exists(key)) {
      return;
    }

    if (this.currentMusic) {
      this.currentMusic.stop();
      this.currentMusic.destroy();
    }

    const volumeSetting = this.settings.getSetting("musicVolume");
    const musicVolume = typeof volumeSetting === "number"
      ? volumeSetting
      : 0.125;
    this.currentMusic = this.game.sound.add(key, {
      loop: true,
      volume: musicVolume,
    });
    this.currentMusic.play();
  };

  public stopMusic = () => {
    if (this.currentMusic) {
      this.currentMusic.stop();
      this.currentMusic.destroy();
      this.currentMusic = null;
    }
  };

  public setMusicVolume = (volume: number) => {
    this.settings.setSetting("musicVolume", volume);
    if (this.currentMusic) {
      this.currentMusic.setVolume(volume);
    }
  };

  public stopAllMusic = () => {
    this.stopMusic();
  };
}
